// ===================================================================
// 🏛️ GERENCIADOR DE INSTITUIÇÕES - Painel Master (Proprietário)
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { InstitutionUsersPage } from './InstitutionUsersPage';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  SELECTION_MODE_CATALOG,
  DEFAULT_CONTRACTED_MODES,
  getSelectionModeLabel,
} from '../../utils/selectionMode';
import { tenantAccessUrl, tenantAccessHost } from '../../utils/helpers';

export const InstitutionManager = React.memo(() => {
  const [institutions, setInstitutions] = useState([]);
  const [ownerInst, setOwnerInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ownerForm, setOwnerForm] = useState({ name: '', slug: '' });
  const [usersPageInst, setUsersPageInst] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    is_active: true,
    contracted_selection_modes: [...DEFAULT_CONTRACTED_MODES],
    candidate_pwa_contracted: false,
  });
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizeInst, setFinalizeInst] = useState(null);
  const [finalizeSlug, setFinalizeSlug] = useState('');
  const [finalizeConfirmed, setFinalizeConfirmed] = useState(false);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportInst, setExportInst] = useState(null);
  const [exportPreview, setExportPreview] = useState(null);
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);
  const [exportConfirmLoading, setExportConfirmLoading] = useState(false);
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [clients, owner] = await Promise.all([
        apiClient.getMasterInstitutions(),
        apiClient.getMasterOwnerInstitution().catch(() => null),
      ]);
      setInstitutions(Array.isArray(clients) ? clients : []);
      setOwnerInst(owner);
    } catch (err) {
      setError(err.message || 'Erro ao carregar instituições');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'name' && !editing) {
      const slug = (value || '').toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
    if (formError) setFormError('');
  }, [editing, formError]);

  const handleOpenCreate = useCallback(() => {
    setEditing(null);
    setFormData({ name: '', slug: '', is_active: true, contracted_selection_modes: [...DEFAULT_CONTRACTED_MODES], candidate_pwa_contracted: false });
    setFormError('');
    setShowModal(true);
  }, []);

  const handleOpenEdit = useCallback((inst) => {
    setEditing(inst);
    setFormData({
      name: inst.name,
      slug: inst.slug,
      is_active: inst.is_active,
      contracted_selection_modes: Array.isArray(inst.contracted_selection_modes) && inst.contracted_selection_modes.length
        ? [...inst.contracted_selection_modes]
        : [...DEFAULT_CONTRACTED_MODES],
      candidate_pwa_contracted: !!inst.candidate_pwa_contracted,
    });
    setFormError('');
    setShowModal(true);
  }, []);

  const handleOpenOwnerEdit = useCallback(() => {
    if (!ownerInst) return;
    setOwnerForm({ name: ownerInst.name || '', slug: ownerInst.slug || '' });
    setFormError('');
    setShowOwnerModal(true);
  }, [ownerInst]);

  const handleOwnerSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!ownerForm.name?.trim() || !ownerForm.slug?.trim()) {
      setFormError('Nome e slug são obrigatórios');
      return;
    }
    const slug = ownerForm.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      const updated = await apiClient.updateMasterOwnerInstitution({
        name: ownerForm.name.trim(),
        slug,
      });
      setOwnerInst(updated);
      setShowOwnerModal(false);
      addNotification(
        'Sucesso',
        `Ambiente proprietário atualizado. Acesse por ${tenantAccessHost(updated.slug)}`,
        'success'
      );
    } catch (err) {
      setFormError(err.message || 'Erro ao salvar');
    }
  }, [ownerForm, addNotification]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setFormError('Nome é obrigatório');
      return;
    }
    if (!formData.slug?.trim()) {
      setFormError('Slug é obrigatório (ex: ifnmg, fapemig)');
      return;
    }
    if (!formData.contracted_selection_modes?.length) {
      setFormError('Selecione ao menos uma modalidade contratada');
      return;
    }
    const slug = formData.slug.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    if (!slug) {
      setFormError('Slug inválido');
      return;
    }
    try {
      if (editing) {
        await apiClient.updateMasterInstitution(editing.id, {
          name: formData.name.trim(),
          slug,
          is_active: formData.is_active,
          contracted_selection_modes: formData.contracted_selection_modes,
          candidate_pwa_contracted: formData.candidate_pwa_contracted,
        });
        addNotification('Sucesso', 'Instituição atualizada', 'success');
      } else {
        await apiClient.createMasterInstitution({
          name: formData.name.trim(),
          slug,
          is_active: formData.is_active,
          contracted_selection_modes: formData.contracted_selection_modes,
          candidate_pwa_contracted: formData.candidate_pwa_contracted,
        });
        addNotification('Sucesso', 'Instituição criada', 'success');
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setFormError(err.message || 'Erro ao salvar');
    }
  }, [formData, editing, loadData, addNotification]);

  const handleOpenExport = useCallback(async (inst) => {
    setExportInst(inst);
    setExportPreview(null);
    setShowExportModal(true);
    setExportPreviewLoading(true);
    try {
      const preview = await apiClient.getMasterExportPreview(inst.id);
      setExportPreview(preview);
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao carregar prévia', 'error');
      setShowExportModal(false);
    } finally {
      setExportPreviewLoading(false);
    }
  }, [addNotification]);

  const handleConfirmExport = useCallback(async () => {
    if (!exportInst) return;
    const inst = exportInst;
    setShowExportModal(false);
    setExportInst(null);
    setExportPreview(null);
    try {
      const { blob, filename } = await apiClient.exportMasterInstitution(inst.id, inst.slug);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 8000);
      addNotification('Sucesso', 'Exportação concluída. Verifique sua pasta de downloads.', 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao exportar', 'error');
    }
  }, [exportInst, addNotification]);

  const handleOpenFinalize = useCallback((inst) => {
    setFinalizeInst(inst);
    setFinalizeSlug('');
    setFinalizeConfirmed(false);
    setShowFinalizeModal(true);
  }, []);

  const handleFinalize = useCallback(async (e) => {
    e.preventDefault();
    if (!finalizeInst || !finalizeConfirmed) return;
    const slug = (finalizeSlug || '').trim();
    if (slug !== finalizeInst.slug) {
      addNotification('Erro', 'Slug incorreto. Digite exatamente: ' + finalizeInst.slug, 'error');
      return;
    }
    try {
      setFinalizeLoading(true);
      await apiClient.finalizeMasterContract(finalizeInst.id, slug);
      addNotification('Sucesso', 'Contrato encerrado com segurança.', 'success');
      setShowFinalizeModal(false);
      setFinalizeInst(null);
      loadData();
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao encerrar contrato', 'error');
    } finally {
      setFinalizeLoading(false);
    }
  }, [finalizeInst, finalizeSlug, finalizeConfirmed, loadData, addNotification]);

  if (loading && !usersPageInst) {
    return <LoadingSpinner text="Carregando instituições..." />;
  }

  if (usersPageInst) {
    return (
      <InstitutionUsersPage
        institution={usersPageInst}
        onBack={() => setUsersPageInst(null)}
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1F2937' }}>🏛️ Instituições clientes</h2>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#6B7280' }}>
            Contratantes do sistema (IFNMG, etc.). O ambiente proprietário não aparece aqui.
          </p>
        </div>
        <button onClick={handleOpenCreate} style={{ ...styles.button, ...styles.buttonPrimary }}>
          + Nova instituição cliente
        </button>
      </div>

      {error && (
        <Alert type="error" message={error} onClose={() => setError('')} />
      )}

      {ownerInst && (
        <div
          style={{
            marginBottom: '24px',
            padding: '16px 20px',
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: '10px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1D4ED8', textTransform: 'uppercase' }}>
              Seu ambiente (proprietário da plataforma)
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1E3A8A', marginTop: '4px' }}>
              {ownerInst.name}
            </div>
            <div style={{ fontSize: '13px', color: '#374151', marginTop: '4px' }}>
              Slug: <code style={{ backgroundColor: '#DBEAFE', padding: '2px 6px', borderRadius: 4 }}>{ownerInst.slug}</code>
              {' · '}
              <a href={tenantAccessUrl(ownerInst.slug)} style={{ color: '#2563EB' }}>
                {tenantAccessHost(ownerInst.slug)}
              </a>
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
              CNPJ, razão social e ícone: menu <strong>Configurações</strong>. Dados legais na aba <strong>Sobre</strong>.
            </div>
          </div>
          <button
            type="button"
            onClick={handleOpenOwnerEdit}
            style={{ ...styles.button, ...styles.buttonPrimary, flexShrink: 0 }}
          >
            Editar nome / slug
          </button>
        </div>
      )}

      <div style={{ ...styles.tableWrapper, overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Nome</th>
              <th style={styles.tableHeaderCell}>Slug (subdomínio)</th>
              <th style={styles.tableHeaderCell}>Modalidades contratadas</th>
              <th style={styles.tableHeaderCell}>App candidato</th>
              <th style={styles.tableHeaderCell}>Status</th>
              <th style={styles.tableHeaderCell}>URL de acesso</th>
              <th style={styles.tableHeaderCell}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {institutions.map((inst) => (
              <tr key={inst.id} style={styles.tableRow}>
                <td style={styles.tableCell}>{inst.name}</td>
                <td style={styles.tableCell}>
                  <code style={{ backgroundColor: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>{inst.slug}</code>
                </td>
                <td style={{ ...styles.tableCell, maxWidth: 280 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(inst.contracted_selection_modes || DEFAULT_CONTRACTED_MODES).map((mid) => (
                      <span
                        key={mid}
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          borderRadius: 999,
                          backgroundColor: '#EEF2FF',
                          color: '#3730A3',
                          fontWeight: 600,
                        }}
                      >
                        {getSelectionModeLabel(mid)}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: inst.candidate_pwa_contracted ? '#DBEAFE' : '#F3F4F6',
                    color: inst.candidate_pwa_contracted ? '#1D4ED8' : '#6B7280',
                  }}>
                    {inst.candidate_pwa_contracted ? 'Com app (+20%)' : 'Sem app'}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: inst.is_active ? '#D1FAE5' : '#FEE2E2',
                    color: inst.is_active ? '#065F46' : '#991B1B',
                  }}>
                    {inst.is_active ? 'Ativa' : 'Inativa'}
                  </span>
                </td>
                <td style={{ ...styles.tableCell, fontSize: '12px', color: '#6B7280' }}>
                  <a href={tenantAccessUrl(inst.slug)} style={{ color: '#2563EB' }}>
                    {tenantAccessHost(inst.slug)}
                  </a>
                </td>
                <td style={styles.tableCell}>
                  <button
                    onClick={() => setUsersPageInst(inst)}
                    style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall, marginRight: '4px' }}
                  >
                    Usuários
                  </button>
                  <button
                    onClick={() => handleOpenEdit(inst)}
                    style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall, marginRight: '4px' }}
                  >
                    Editar
                  </button>
                  {inst.slug !== 'master' && (
                    <>
                      <button
                        onClick={() => handleOpenExport(inst)}
                        style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall, marginRight: '4px' }}
                        title="Exportar dados e documentos (ZIP)"
                      >
                        Exportar
                      </button>
                      {inst.is_active && (
                        <button
                          onClick={() => handleOpenFinalize(inst)}
                          style={{
                            ...styles.button,
                            ...styles.buttonSmall,
                            backgroundColor: '#B91C1C',
                            color: '#FFF',
                            border: 'none',
                          }}
                          title="Encerrar contrato (remove dados e marca instituição inativa)"
                        >
                          Encerrar
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {institutions.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          <p>Nenhuma instituição cadastrada.</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>Clique em "Nova instituição" para adicionar.</p>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Editar instituição' : 'Nova instituição'}
      >
        <form onSubmit={handleSubmit}>
          {formError && (
            <div style={{ marginBottom: '16px' }}>
              <Alert type="error" message={formError} />
            </div>
          )}
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Sigla da instituição</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Ex: IFNMG, Fapemig"
              style={styles.input}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Slug (subdomínio)</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              placeholder="Ex: ifnmg, fapemig"
              style={styles.input}
            />
            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              Usado na URL: {tenantAccessHost(formData.slug || 'slug')}
            </p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Modalidades contratadas *</label>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 10px' }}>
              Define o que esta instituição pode usar ao criar processos seletivos. Barema e sorteio podem ser
              contratadas antecipadamente; a criação de processos só libera modalidades já implementadas no sistema.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {SELECTION_MODE_CATALOG.map((mode) => {
                const checked = formData.contracted_selection_modes.includes(mode.id);
                return (
                  <label
                    key={mode.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #E5E7EB',
                      backgroundColor: checked ? '#F0FDF4' : '#FAFAFA',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        setFormData((prev) => {
                          const set = new Set(prev.contracted_selection_modes);
                          if (e.target.checked) set.add(mode.id);
                          else set.delete(mode.id);
                          return { ...prev, contracted_selection_modes: [...set] };
                        });
                        if (formError) setFormError('');
                      }}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ fontWeight: 600, color: '#111827' }}>{mode.label}</span>
                      {!mode.implemented && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#B45309', fontWeight: 600 }}>
                          em breve
                        </span>
                      )}
                      <span style={{ display: 'block', fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                        {mode.description}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
          <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: 8, border: '1px solid #E5E7EB', backgroundColor: '#FAFAFA' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.candidate_pwa_contracted}
                onChange={(e) => handleInputChange('candidate_pwa_contracted', e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                <span style={{ fontWeight: 600, color: '#111827' }}>Aplicativo candidato (PWA)</span>
                <span style={{ display: 'block', fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Instalação na tela inicial do celular e notificações push nos selos da inscrição.
                  Plano com acréscimo de cerca de 20% sobre o valor base.
                </span>
              </span>
            </label>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
              <span>Instituição ativa</span>
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setShowModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancelar
            </button>
            <button type="submit" style={{ ...styles.button, ...styles.buttonPrimary }}>
              {editing ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showFinalizeModal}
        onClose={() => !finalizeLoading && setShowFinalizeModal(false)}
        title="Encerrar contrato"
      >
        {finalizeInst && (
          <form onSubmit={handleFinalize}>
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FECACA' }}>
              <p style={{ margin: 0, color: '#991B1B', fontWeight: '600' }}>
                Esta ação é irreversível. Todos os dados da instituição serão removidos.
              </p>
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#7F1D1D' }}>
                Serão excluídos: inscrições, análises, pagamentos, documentos, processos, usuários e demais dados.
              </p>
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#7F1D1D' }}>
                A instituição será marcada como inativa. Antes de encerrar, exporte os dados se necessário.
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={finalizeConfirmed}
                  onChange={(e) => setFinalizeConfirmed(e.target.checked)}
                />
                <span>Entendo que esta ação é irreversível e desejo encerrar o contrato de <strong>{finalizeInst.name}</strong></span>
              </label>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={styles.label}>Digite o slug para confirmar: <code>{finalizeInst.slug}</code></label>
              <input
                type="text"
                value={finalizeSlug}
                onChange={(e) => setFinalizeSlug(e.target.value)}
                placeholder={finalizeInst.slug}
                style={styles.input}
                disabled={!finalizeConfirmed}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowFinalizeModal(false)} disabled={finalizeLoading} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancelar
              </button>
              <button
                type="submit"
                disabled={!finalizeConfirmed || (finalizeSlug || '').trim() !== finalizeInst.slug || finalizeLoading}
                style={{ ...styles.button, backgroundColor: '#B91C1C', color: '#FFF', border: 'none' }}
              >
                {finalizeLoading ? 'Encerrando...' : 'Encerrar contrato'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        isOpen={showOwnerModal}
        onClose={() => setShowOwnerModal(false)}
        title="Editar ambiente proprietário"
      >
        <form onSubmit={handleOwnerSubmit}>
          {formError && (
            <div style={{ marginBottom: '16px' }}>
              <Alert type="error" message={formError} />
            </div>
          )}
          <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
            Isto é o <strong>seu</strong> ambiente (proprietário), não uma instituição cliente. Ao mudar o slug, use a nova URL para entrar.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Nome</label>
            <input
              type="text"
              value={ownerForm.name}
              onChange={(e) => setOwnerForm((p) => ({ ...p, name: e.target.value }))}
              style={styles.input}
              placeholder="Ex: Vida de Silício"
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={styles.label}>Slug (subdomínio)</label>
            <input
              type="text"
              value={ownerForm.slug}
              onChange={(e) => setOwnerForm((p) => ({ ...p, slug: e.target.value }))}
              style={styles.input}
              placeholder="Ex: vds"
            />
            <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              URL: {tenantAccessHost(ownerForm.slug || 'slug')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setShowOwnerModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancelar
            </button>
            <button type="submit" style={{ ...styles.button, ...styles.buttonPrimary }}>
              Salvar
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={showExportModal}
        onClose={() => !exportConfirmLoading && setShowExportModal(false)}
        title="Exportar instituição"
      >
        {exportInst && (
          <div>
            {exportPreviewLoading ? (
              <LoadingSpinner text="Carregando resumo..." />
            ) : exportPreview ? (
              <>
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                  <p style={{ margin: 0, fontWeight: '600', color: '#1E40AF' }}>{exportPreview.institution_name}</p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#1E3A8A' }}>Slug: {exportPreview.institution_slug}</p>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', margin: '0 0 8px', color: '#374151' }}>Conteúdo da exportação</h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#4B5563', lineHeight: 1.8 }}>
                    <li><strong>data.json</strong> — Dados completos (inscrições, processos, usuários, etc.)</li>
                    <li><strong>{exportPreview.processes}</strong> processo(s) seletivo(s)</li>
                    <li><strong>{exportPreview.applications}</strong> inscrição(ões)</li>
                    <li><strong>{exportPreview.total_files}</strong> arquivo(s) de documento anexado(s)</li>
                  </ul>
                  <div style={{ marginTop: '12px', fontSize: '13px', color: '#6B7280' }}>
                    <p style={{ margin: 0 }}>Documentos incluídos:</p>
                    <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                      {exportPreview.process_documents > 0 && <li>{exportPreview.process_documents} do processo (edital/anexos)</li>}
                      {exportPreview.exemption_documents > 0 && <li>{exportPreview.exemption_documents} de isenção</li>}
                      {exportPreview.appeal_documents > 0 && <li>{exportPreview.appeal_documents} de recurso</li>}
                      {exportPreview.application_documents > 0 && <li>{exportPreview.application_documents} curriculares/da inscrição</li>}
                    </ul>
                  </div>
                </div>
                <p style={{ marginBottom: '16px', fontSize: '13px', color: '#6B7280' }}>
                  ⏱️ Tempo estimado: ~{exportPreview.estimated_seconds} s
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowExportModal(false)} disabled={exportConfirmLoading} style={{ ...styles.button, ...styles.buttonSecondary }}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmExport}
                    disabled={exportConfirmLoading}
                    style={{ ...styles.button, ...styles.buttonPrimary }}
                  >
                    {exportConfirmLoading ? 'Gerando ZIP...' : 'Confirmar e baixar ZIP'}
                  </button>
                </div>
              </>
            ) : (
              <p style={{ color: '#6B7280' }}>Erro ao carregar prévia.</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
});
